// src/components/AITools.jsx — In-app guide for AI enhancement (Autopilot, Guided, form enhance, access, verification)
import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Sparkles,
  Rocket,
  Target,
  Mail,
  RefreshCw,
  WifiOff,
  Crown,
  BookOpen,
} from 'lucide-react';

function AITools() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Ideas
        </Link>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 md:p-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            AI tools in Epiphoric
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
            How Autopilot, Guided Journey, and &ldquo;Enhance with AI&rdquo; work — and what you need to use them
          </p>

          <div className="prose prose-gray dark:prose-invert max-w-none space-y-10">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-purple-500" aria-hidden />
                What &ldquo;AI tools&rdquo; means here
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                Epiphoric can use AI to turn a rough description into a structured idea: title, description, tags,
                tasks, and an optional plan. That capability is{' '}
                <strong className="text-gray-900 dark:text-white">optional</strong> — capturing and organizing ideas
                does not require AI.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                You&apos;ll see AI in a few places: when you <strong>choose a path</strong> after quick capture
                (Autopilot or Guided Journey), when you tap <strong>Develop</strong> on an existing idea, when you use{' '}
                <strong>Enhance with AI</strong> on the add/edit idea form, and in export flows that generate
                AI-assisted content. All of these respect the same account rules below.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Rocket className="w-6 h-6 text-purple-500" aria-hidden />
                Autopilot
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                One pass: AI proposes title, description, tags, subtasks, and plan steps from your text. Best when you
                want a finished draft quickly.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Target className="w-6 h-6 text-indigo-500" aria-hidden />
                Guided Journey
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                Three guided steps so you can refine title and description, then tags and tasks, then the plan — with
                chances to edit and regenerate along the way.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-primary" aria-hidden />
                Enhance with AI (idea form)
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                From the add or edit idea screen, <strong>Enhance with AI</strong> runs enhancement on your description
                and opens a preview modal so you can apply or tweak results before saving.
              </p>
            </section>

            <section className="border border-amber-200 dark:border-amber-800 rounded-lg p-5 bg-amber-50/80 dark:bg-amber-900/15">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Crown className="w-6 h-6 text-amber-600 dark:text-amber-400" aria-hidden />
                Access, complimentary credit, and paid plan
              </h2>
              <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300">
                <li>
                  AI features require an <strong>active entitlement</strong> for your account — for example a{' '}
                  <strong>complimentary enhancement credit</strong>, a paid subscription, or operator-granted access.
                </li>
                <li>
                  Under typical policy, <strong>one complimentary credit</strong> applies to <strong>either</strong>{' '}
                  Autopilot <strong>or</strong> completing Guided Journey (not both on that single credit alone).
                </li>
                <li>
                  <strong>Raw Save</strong> and normal idea editing do not use an AI credit.
                </li>
                <li>
                  Paid access is <strong>$10/month</strong> and includes <strong>100 AI enhancement credits</strong>{' '}
                  per billing period. Autopilot uses <strong>1 credit</strong>; a full Guided Journey uses up to{' '}
                  <strong>3 credits</strong> (one per step). Credits refill when your billing period renews; unused
                  credits do not roll over.
                </li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mt-4">
                Subscribe or manage billing from{' '}
                <Link to="/support" className="text-primary font-medium hover:underline">
                  Support
                </Link>{' '}
                or the Path Selector when your complimentary credit is used.
              </p>
            </section>

            <section className="border border-green-200 dark:border-green-800 rounded-lg p-5 bg-green-50/50 dark:bg-green-900/15">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Mail className="w-6 h-6 text-green-600 dark:text-green-400" aria-hidden />
                Verified email and the complimentary AI pass
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                If your access is through the <strong>complimentary</strong> pass, the service requires a{' '}
                <strong>verified email address</strong> (we email you a confirmation link; you confirm by clicking it). That
                reduces abuse and ties the credit to a real inbox.
              </p>
              <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300">
                <li>
                  Check status and resend verification from{' '}
                  <Link to="/profile" className="text-primary font-medium hover:underline">
                    My Account
                  </Link>
                  .
                </li>
                <li>
                  After you click the link in email, use <strong>I&apos;ve verified — refresh</strong> where offered, or
                  sign out and back in so your session picks up <code className="text-sm">emailVerified</code>.
                </li>
                <li>
                  Paid or other non-complimentary entitlements follow the same product rules documented on{' '}
                  <Link to="/support" className="text-primary font-medium hover:underline">
                    Support
                  </Link>
                  ; verification messaging appears when you&apos;re on the complimentary path.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <WifiOff className="w-6 h-6 text-gray-500" aria-hidden />
                &ldquo;Could not load enhancement status&rdquo; or similar
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                The app checks your entitlement from the server. If that check fails (network, Firestore rules, or a
                transient error), buttons like <strong>Enhance with AI</strong> may stay disabled and you may see a
                short error under the control.
              </p>
              <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300">
                <li>
                  Confirm you&apos;re online, then <strong>refresh the page</strong>.
                </li>
                <li>
                  Try <strong>sign out</strong> and sign back in to refresh your token and subscription.
                </li>
                <li>
                  If it persists, contact{' '}
                  <Link to="/support" className="text-primary font-medium hover:underline">
                    Support
                  </Link>{' '}
                  with what you were doing and approximate time.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <RefreshCw className="w-6 h-6 text-primary" aria-hidden />
                Stale &ldquo;access denied&rdquo; right after something changed
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                If you just verified your email or your entitlement updated, the UI might need a moment. Refresh the
                page or sign out and in. Some flows also refresh your session when an error code indicates stale data.
              </p>
            </section>

            <section className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" aria-hidden />
                Related
              </h2>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li>
                  <Link to="/how-it-works" className="text-primary font-medium hover:underline">
                    How Epiphoric works
                  </Link>{' '}
                  — broader product tour, including AI overview
                </li>
                <li>
                  <Link to="/support" className="text-primary font-medium hover:underline">
                    Support
                  </Link>{' '}
                  — entitlement FAQs and contact
                </li>
                <li>
                  <Link to="/terms" className="text-primary font-medium hover:underline">
                    Terms of Service
                  </Link>{' '}
                  — AI and entitlement terms
                </li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AITools;
